import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { NavLink } from '@remix-run/react';
import { GQLFullCtaFragment } from '@theprintbar/graphql';
import { ImageSize, cn } from '@theprintbar/shared';
import { Breadcrumbs, Container, PromoImageContainer, RichText } from '@theprintbar/ui';
import { Fragment } from 'react/jsx-runtime';

interface BannerHeaderProps {
	cta: GQLFullCtaFragment['fullCTA'];
	hideText?: boolean;
	bannerTextClassName?: string;
	breadcrumbs?: {
		name: string;
		href: string;
		current: boolean;
	}[];
}

export function BannerHeader({ cta, hideText, breadcrumbs, bannerTextClassName }: BannerHeaderProps) {
	if (!cta.structure) return null;

	const { image, title, text } = cta.structure;
	return (
		<div className="mb-2 md:mb-4 lg:mb-8">
			<PromoImageContainer className="h-44 lg:h-60 w-full" lazyLoad={false} size={ImageSize.Original} promoImage={image} />

			{!hideText && (
				<Container className={cn('mt-4', bannerTextClassName)}>
					{!!breadcrumbs?.length && (
						<nav className="flex mt-2" aria-label="Breadcrumb">
							<ol className="flex items-center space-x-2">
								{breadcrumbs?.map((crumb, index) => (
									<Fragment key={crumb.href}>
										{!!index && <ChevronRightIcon className="flex-shrink-0 h-4 w-4 text-gray-400" aria-hidden="true" />}
										<li>
											<div className="flex items-center">
												<NavLink
													to={crumb.href}
													className={({ isActive }) =>
														cn(
															'max-w-[10rem] truncate',
															isActive
																? 'text-xs font-medium text-gray-400 hover:cursor-default hover:text-gray-400'
																: 'text-xs font-medium text-gray-600 hover:text-gray-700',
														)
													}
												>
													{crumb.name}
												</NavLink>
											</div>
										</li>
									</Fragment>
								))}
							</ol>
						</nav>
					)}
					<div className={cn('flex flex-col lg:flex-row lg:items-center lg:gap-32', !!breadcrumbs?.length ? 'mt-2' : 'mt-5')}>
						<h4 className="font-display font-light text-2xl lg:text-3xl break-words normal-case mb-4 lg:mb-0 lg:max-w-lg">
							{title}
						</h4>
						<RichText className="space-y-2 text-sm lg:text-base max-w-3xl" {...text} />
					</div>
				</Container>
			)}
		</div>
	);
}
